/**
 * A queued item's place, in words, for a pipeline whose runner may take one batch at a
 * time (Qawl's lander). There, the next item waits for the current batch, not for a free
 * worker: the gauge can show free workers while the queue does not move.
 */
import type { Pipeline, PipelineBatch, PipelineItem } from '@orgus/protocol';

const count = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;

/** "Next: waits for the current batch (9 submissions)", "Next", or "#3 in queue". */
export function queuePlace(position: number, batch: PipelineBatch | undefined): string {
  if (position !== 1) return `#${position} in queue`;
  if (!batch || batch.size <= 0) return 'Next';
  return `Next: waits for the current batch (${count(batch.size, batch.unit || 'item')})`;
}

/**
 * Does this queued item hold a place in the queue? With a batch in progress, an item
 * waiting in a later stage belongs to that batch (a failed batch retried in smaller
 * groups), not to the queue behind it.
 */
export function holdsQueuePlace(p: Pick<Pipeline, 'batch' | 'stages'>, item: Pick<PipelineItem, 'stage'>): boolean {
  return !p.batch || item.stage === p.stages[0]?.id;
}
